<template>

	<div v-if="!hasCurriculum" class="alert alert-warning m-4 whitespace-pre-line">
		{{ $t('#instruction: curriculum not set') }}
	</div>

	<GoalFilter
		v-else-if="loaded"
		class="py-2 border-b"
		:filter="filter"
		:initialFilter="initialFilter"
		:goalSources="goalsStore.allSources"
		:collapsable="true"
		:collapsed="false"
		@change="applyFilter"
		:withGoalCollectionOptions="withGoalCollectionOptions"
		:withSchedulingSegmentsOptions="withSchedulingSegmentsOptions"
		:allScheduledGoalsLabel="allScheduledGoalsLabel"
	/>

	<GoalsPupilsMatrix
		v-if="hasCurriculum"
		ref="matrix"
		:goalStructures="goalsStore.structuredGoals"
		@selectEvaluation="openEvaluationEditor"
		@selectionChange="onSelectionChange"
		@bulkEvaluate="openBulkEditor"
		:loading="loading"
		:showEmptyState="goalsStore.hasDefaultFilterApplied"
	>
		<template #goal="{ goal, goalSearchResult }">
			<GoalTitle :goal="goal" :goalSearchResult="goalSearchResult" :showPath="true" />

			<div v-if="goalSearchResult.goal.tags?.length" class="flex gap-2 mt-1">
				<Tag v-for="tag in goalSearchResult.goal.tags" class="tag-xs">{{ tag }}</Tag>
			</div>
		</template>
	</GoalsPupilsMatrix>

	<EvaluationEditor
		v-if="showEvaluationForm"
		@requestClose="closeEvaluationForm()"
		:pupil="evaluationFormPupil"
		:goal="evaluationFormGoal"
	></EvaluationEditor>

	<BulkEvaluationEditor
		v-if="showBulkEditor"
		:pairs="bulkPairs"
		:allPupils="bulkAllPupils"
		@saved="onBulkSaved"
		@requestClose="showBulkEditor = false"
	/>

</template>

<script lang="ts">

import Drawer from "@/components/ui/Drawer.v2.vue";
import { Goal } from "@/models/Goal.model";
import { Pupil } from "@/models/Pupil.model";
import { Curriculum } from "@/models/Curriculum.model";
import { useEvaluationLevelsStore } from "@/stores/EvaluationLevels.store";
import { useEvaluationsStore } from "@/stores/Evaluations.store";
import {GOAL_SCOPES, GoalFilterDef} from "@/stores/filters/GoalFilterDef";
import { usePupilsStore } from "@/stores/Pupils.store";
import { Interval } from "luxon";
import { mapStores } from "pinia";
import GoalsPupilsMatrix from "@/components/GoalsPupilsMatrix.vue";
import EvaluationEditor from "./EvaluationEditor.vue";
import BulkEvaluationEditor from "./BulkEvaluationEditor.vue";
import type { BulkEvaluationPair } from "./BulkEvaluationEditor.vue";
import GoalTitle from "@/components/GoalTitle.vue";
import GoalFilter from "@/components/GoalFilter.vue";
import {useGoalsStore} from "@/stores/Goals.store.ts";
import {useOrganisationsStore} from "@/stores/Organisations.store.ts";
import { useClassroomsStore } from "@/stores/Classrooms.store.ts";
import Tag from "@/components/ui/Tag.vue";

export default {
	components: {
		Tag,
		GoalFilter,
		Drawer,
		GoalsPupilsMatrix,
		EvaluationEditor,
		BulkEvaluationEditor,
		GoalTitle
	},

	props: {
		title: {
			type: String,
			default: '',
		},
		dateRange: {
			type: Interval,
			required: false,
		},
		pupilIds: {
			type: Array as () => string[],
			required: false,
		},
		withSchedulingSegmentsOptions: {
			type: Boolean,
			default: false,
		},
		withGoalCollectionOptions: {
			type: Boolean,
			default: true,
		},
		allScheduledGoalsLabel: {
			type: String,
			default: null,
		},
	},

	data() {
		return {
			loading: false,
			loaded: false,
			filter: null as GoalFilterDef | null,
			initialFilter: null as GoalFilterDef | null,
			showEvaluationForm: false,
			evaluationFormPupil: null as Pupil | null,
			evaluationFormGoal: null as Goal | null,
			showBulkEditor: false,
			bulkPairs: [] as BulkEvaluationPair[],
			bulkAllPupils: [] as Pupil[],
		}
	},

	emits: [
		'requestUnmount',
	],

	computed: {
		...mapStores(
			usePupilsStore,
			useEvaluationLevelsStore,
			useEvaluationsStore,
			useGoalsStore,
			useOrganisationsStore,
			useClassroomsStore
		),

		/** Goals are only ever consulted or evaluated for the school's own curriculum. */
		schoolCurriculum(): Curriculum | null {
			return this.organisationsStore.currentOrganisation?.curriculum ?? null;
		},

		hasCurriculum(): boolean {
			return this.schoolCurriculum !== null;
		},
	},

	async created() {

		// Without a curriculum there is nothing a teacher may evaluate: show the notice only.
		if (!this.hasCurriculum) {
			this.loaded = true;
			return;
		}

		this.loading = true;

		// Planned goals may come from other curricula (external plans, or plans kept after a
		// curriculum switch). Teachers only ever see and evaluate the school's own curriculum,
		// so the shared store runs in single-curriculum mode here (this also keeps collection
		// goals out).
		this.goalsStore.setCurriculum(this.schoolCurriculum);

		// This page always runs in classroom context.
		this.filter = this.goalsStore.getDefaultEvaluationsFilter(this.classroomsStore.getCurrentClassroom());
		if (!this.filter) {
			throw new Error('GoalsStore did not return a default filter');
		}

		// Set pupil ids
		await this.evaluationLevelsStore.loadOnce();

		if (this.dateRange) {
			this.filter.setInterval(this.dateRange);

			// The date range selects the goals planned in it, but the matrix should show each
			// pupil's current evaluation, not only the ones made within the range: load the
			// latest evaluation made up to the end of the range.
			this.filter.setEvaluationsRange(null, this.dateRange.end?.toJSDate() ?? null);
		}

		if (this.pupilIds) {
			this.filter.pupilIds = this.pupilIds;
		} else {
			await this.pupilsStore.loadAllForClassroom();
			this.filter.pupilIds = this.pupilsStore.items.map((pupil: Pupil) => pupil.id);
		}

		this.filter.goalScopes = [ GOAL_SCOPES.PLANNED ];

		this.initialFilter = this.filter.clone();
		await this.applyFilter(this.filter, true);

		this.loading = false;
		this.loaded = true;

	},

	methods: {

		openEvaluationEditor(pupil: Pupil, goal: Goal) {
			this.showEvaluationForm = true;

			this.evaluationFormPupil = pupil;
			this.evaluationFormGoal = goal;

		},

		closeEvaluationForm() {
			this.showEvaluationForm = false;
		},

		onSelectionChange(pairs: BulkEvaluationPair[]) {
			this.bulkPairs = pairs;
		},

		openBulkEditor(pairs: BulkEvaluationPair[], allPupils: Pupil[]) {
			this.bulkPairs = pairs;
			this.bulkAllPupils = allPupils;
			this.showBulkEditor = true;
		},

		onBulkSaved() {
			this.showBulkEditor = false;
			(this.$refs.matrix as any)?.exitBulkMode();
		},


		async applyFilter(filter: GoalFilterDef, forceReload = false) {

			this.filter = filter;
			this.loading = true;

			await this.goalsStore.applyFilter(this.filter, forceReload);

			this.loading = false;

		}

	},
}

</script>
